import { IOperationResultNoValue, ObservableCollection, TagViewModel, TagsViewModel } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import Waypoint from 'react-waypoint';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../models/Logging';
import { DataRefreshViewModel } from '../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../web/components/Checkbox';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { TagsGraphic } from '../../../web/components/svgs/graphics/TagsGraphic';
import { Tag } from '../../../web/components/tags/Tag';
import { baseStyleSheet } from '../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps, IErrorMessageComponentProps {
	canSelectAll?: boolean;
	className?: string;
	dataRefreshVm: DataRefreshViewModel;
	onClearSelectedTags?(tags: TagViewModel[]): void;
	styles?: StyleDeclarationValue[];
	withPolicyTags?: boolean;
}

interface IState {
	fullListMode?: boolean;
	initialTags?: ObservableCollection<TagViewModel>;
	withPolicyTags?: boolean;
}

class _TagSelector extends React.Component<IProps, IState> {
	private selectedTags = new ObservableCollection<TagViewModel>([], 'id');
	private tagsVM: TagsViewModel = null;
	public constructor(props: IProps) {
		super(props);

		makeObservable<_TagSelector, 'selectedTags' | 'tagsVM'>(this, {
			selectedTags: observable.ref,
			tagsVM: observable.ref,
		});

		const initialTags = new ObservableCollection(props.dataRefreshVm?.policyTags?.toArray() || [], 'id');

		this.state = {
			fullListMode: !initialTags?.length,
			initialTags,
			withPolicyTags: props.withPolicyTags,
		};
	}

	public static getDerivedStateFromProps({ dataRefreshVm, withPolicyTags }: IProps, prevState: IState) {
		let nextState = prevState;

		if (withPolicyTags !== prevState.withPolicyTags) {
			const initialTags = new ObservableCollection(dataRefreshVm?.policyTags?.toArray() || [], 'id');
			nextState = {
				...nextState,
				fullListMode: !initialTags?.length,
				initialTags,
				withPolicyTags,
			};
		}

		return nextState;
	}

	public render() {
		const { className, styles, dataRefreshVm } = this.props;
		const { fullListMode, initialTags } = this.state;

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} tag-selector ${className || ''}`}>
				<div className={css(styleSheet.header)}>
					<div>
						<Checkbox
							disabled={fullListMode}
							className={css(styleSheet.selectAllCheckbox)}
							checked={
								this.selectedTags.length === initialTags?.length && !(this.selectedTags.length === 0) && !fullListMode
							}
							id='select-tags-to-remove-data-refresh'
							name='select-tags-to-remove-data-refresh'
							onChange={this.onSelectionChanged}
							type='large'
						>
							<div className={css(styleSheet.selectAllText)}>Select All</div>
						</Checkbox>
					</div>
					<div className={css(styleSheet.contactsTitle)}>Contacts</div>
					<div className={css(styleSheet.buttonContainer)}>
						<button
							className={css(baseStyleSheet.ctaButtonSmall)}
							disabled={!this.selectedTags?.length}
							onClick={this.onClearSelectedTag}
						>
							<span>Delete all selected tags</span>
						</button>
					</div>
				</div>
				<div className={css(styleSheet.listArea)}>
					{fullListMode
						? this.tagsVM?.tags?.length
							? this.tagsVM?.tags?.filter(tag => !dataRefreshVm.tagsToDelete.has(tag))?.map(tag => this.renderTag(tag))
							: this.renderNoPolicyTagsFound()
						: initialTags?.length
							? initialTags?.map(tag => this.renderTag(tag))
							: this.renderNoPolicyTagsFound()}
					{fullListMode && !!this.tagsVM?.tags?.length && <Waypoint bottomOffset='-200px' onEnter={this.getMoreTags} />}
					{fullListMode && this.tagsVM?.isFetchingResults && <LoadingSpinner />}
				</div>
			</div>
		);
	}

	private renderNoPolicyTagsFound() {
		const { withPolicyTags } = this.state;
		return (
			<div className={css(styleSheet.empty)}>
				<TagsGraphic />
				<div className={css(styleSheet.noPolicy)}>
					{withPolicyTags ? 'There are no policy tags left.' : 'There were no policy tags found.'}
				</div>
				<div onClick={this.loadTagsVM} className={css(styleSheet.linkBrowse, baseStyleSheet.brandLink)}>
					Browse all tags
				</div>
			</div>
		);
	}

	private getMoreTags = () => {
		const { logApiError, errorMessages } = this.props;
		this.tagsVM.getTags({ sort: 'desc', sortBy: 'stats.contacts' })?.catch((err: IOperationResultNoValue) => {
			logApiError('LoadTags-Error', err);

			errorMessages.pushApiError(err);
		});
	};

	private onClearSelectedTag = () => {
		const { fullListMode, initialTags } = this.state;
		this.props.onClearSelectedTags?.(this.selectedTags.toArray());
		if (fullListMode) {
			this.tagsVM.tags.removeItems(this.selectedTags.toArray());
		} else {
			initialTags.removeItems(this.selectedTags.toArray());
		}
		this.selectedTags.clear();
	};

	private loadTagsVM = () => {
		const { userSession, dataRefreshVm } = this.props;
		this.setState({ fullListMode: true });

		this.tagsVM = new TagsViewModel(userSession);
		this.tagsVM.impersonate({ account: dataRefreshVm.account });
		this.getMoreTags();
	};

	private onSelectionChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { initialTags } = this.state;
		if (e.target.checked) {
			this.selectedTags.addAll(initialTags.toArray());
		} else {
			this.selectedTags.removeItems(initialTags.toArray());
		}
	};

	private onTagChanged = (tag: TagViewModel) => (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			this.selectedTags.add(tag);
		} else {
			this.selectedTags.removeItems([tag]);
		}
	};

	private renderTag(tag: TagViewModel) {
		const checked = this.selectedTags.has(tag);
		return (
			<div className={css(styleSheet.tagRow, checked && styleSheet.tagRowHighlight)} key={tag.id}>
				<Checkbox
					className={css(styleSheet.tagCheckbox)}
					checked={checked}
					id={`select-tags-to-remove-data-refresh-${tag.id}`}
					name={`select-tags-to-remove-data-refresh-${tag.id}`}
					onChange={this.onTagChanged(tag)}
					type='large'
				>
					<Tag tagValue={tag.name} className={css(styleSheet.tag)} />
				</Checkbox>
				<div className={css(styleSheet.contactCount)}>{tag.stats?.contacts}</div>
			</div>
		);
	}
}

// @ts-ignore
const TagSelectorAsObserver = observer(_TagSelector);
const TagSelectorWithLogging = withEventLogging(TagSelectorAsObserver, 'TagSelector');
export const TagSelector = inject(UserSessionViewModelKey, ErrorMessagesViewModelKey)(TagSelectorWithLogging);
